import { createContext, useContext } from "react"
import type { Permission } from "@server/permissions"

export interface AdminRole {
  id: string
  name: string
  permissions: string[]
  isSuper: boolean
}

export interface AdminSession {
  id: string
  email: string
  name: string
  avatarUrl: string | null
  role: AdminRole
}

export const AdminSessionContext = createContext<AdminSession | null>(null)

/** The signed-in admin session (null on the login page / before auth resolves). */
export function useAdminSession(): AdminSession | null {
  return useContext(AdminSessionContext)
}

/** The signed-in admin's email (null on the login page / before auth resolves). */
export function useAdminEmail(): string | null {
  const session = useAdminSession()
  return session?.email ?? null
}

/** Check if the current user has a specific permission */
export function useHasPermission(permission: Permission): boolean {
  const session = useAdminSession()
  if (!session) return false
  if (session.role.isSuper) return true
  return session.role.permissions.includes(permission)
}

/** Check if the current user has any of the specified permissions */
export function useHasAnyPermission(permissions: Permission[]): boolean {
  const session = useAdminSession()
  if (!session) return false
  if (session.role.isSuper) return true
  return permissions.some((p) => session.role.permissions.includes(p))
}

/** Check if the current user is a super admin */
export function useIsSuperAdmin(): boolean {
  const session = useAdminSession()
  return session?.role.isSuper ?? false
}
